/**
 * App-layer keyboard focus trap for Harmony `he-fly-in-panel` when running inside MSX.
 *
 * Why this exists (bug 60002844):
 * MSX hosts this app and runs its own Tabster (v8.2.0) focus manager. That Tabster
 * build ships WITHOUT the Modalizer/Groupper/Mover modules, so it cannot honor an
 * `aria-modal` dialog and it cannot descend Harmony's shadow DOM. When a
 * `he-fly-in-panel_mem` is open, Tabster overrides Harmony's native focus trap and,
 * at the `he-data-grid_mem` shadow boundary, ejects keyboard focus to MSX shell
 * chrome (`<i data-tabster-dummy>` -> `document.body`). That is a WCAG 2.4.3 failure.
 *
 * Strategy:
 *  - OWNER: intercept Tab/Shift+Tab in the capture phase while focus is inside an open
 *    panel, `preventDefault()` + `stopImmediatePropagation()`, and move focus ourselves.
 *    Tab stops are computed at "host granularity": every Harmony control is a single
 *    stop, and an entire `he-data-grid` collapses to one stop (focusing its inner
 *    `<table>`). This avoids the grid's roving-tabindex instability.
 *  - BACKSTOP: Tabster's keydown handler runs on `window` capture (above us) and, for
 *    the grid reverse-exit, consumes the event via `moveWithPhantomDummy` before our
 *    owner can see it. We can't pre-empt that one keydown, but the escape lands on a
 *    Tabster dummy `<i>`; a `focusin` backstop catches that dummy focus and redirects
 *    to the correct neighbouring stop. A one-shot guard prevents the owner from also
 *    acting on the same keydown (which would double-step and skip a stop).
 *
 * Only enabled inside MSX; standalone, Harmony's own trap works and this is inert.
 */

const FLY_IN_PANEL_OPEN_SELECTOR = 'he-fly-in-panel_mem[open], he-fly-in-panel[open]';
const FOCUSABLE_SELECTOR = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]';
const EXCLUDE_SELECTOR = '[data-tabster-dummy],[aria-hidden="true"],[inert]';
const TABSTER_DUMMY_SELECTOR = '[data-tabster-dummy]';
const GRID_TAGS = new Set(['HE-DATA-GRID_MEM', 'HE-DATA-GRID']);

const isGridHost = (el: Element | null): boolean => !!el && GRID_TAGS.has(el.tagName);

/** Returns the deeply-focused element, piercing open shadow roots. */
const getDeepActiveElement = (): Element | null => {
  let active: Element | null = document.activeElement;
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  return active;
};

/** Walks ancestors of a node, crossing shadow boundaries via host elements. */
const getAncestorsAcrossShadow = (node: Node | null): Node[] => {
  const ancestors: Node[] = [];
  let current: Node | null = node;
  while (current) {
    ancestors.push(current);
    const root = current.getRootNode();
    current = current.parentNode ?? (root instanceof ShadowRoot ? root.host : null);
  }
  return ancestors;
};

/** The first open fly-in panel, if any. Panels render in the app's light DOM. */
const getOpenFlyInPanel = (): HTMLElement | null => document.querySelector<HTMLElement>(FLY_IN_PANEL_OPEN_SELECTOR);

const isWithinPanel = (panel: HTMLElement, node: Node | null): boolean => !!node && getAncestorsAcrossShadow(node).includes(panel);

const isVisible = (el: Element): boolean => {
  if (!el.isConnected) return false;
  if (el.getClientRects().length === 0 && (el as HTMLElement).offsetParent === null) return false;
  const style = window.getComputedStyle(el);
  return style.visibility !== 'hidden' && style.display !== 'none';
};

/** The custom-element host of the shadow root the element directly lives in, if any. */
const customElementShadowHost = (el: Node): HTMLElement | null => {
  const root = el.getRootNode();
  if (root instanceof ShadowRoot && root.host && root.host.tagName.includes('-')) {
    return root.host as HTMLElement;
  }
  return null;
};

/**
 * Collects the ordered keyboard tab stops within the panel. Each Harmony control is a
 * single stop; an `he-data-grid` collapses to one stop (its host). Descends open shadow
 * roots and resolves slots so the order matches the rendered (visual) tab order.
 */
const collectPanelTabStops = (panel: HTMLElement): HTMLElement[] => {
  const rawFocusables: HTMLElement[] = [];
  const seen = new WeakSet<Node>();

  const walk = (node: Node | null): void => {
    if (!node || seen.has(node)) return;
    seen.add(node);

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (isGridHost(el)) {
        if (isVisible(el)) rawFocusables.push(el);
        return; // grid is opaque: collapse its internals to this single stop
      }
      if (el.matches?.(FOCUSABLE_SELECTOR) && !el.closest?.(EXCLUDE_SELECTOR) && !el.matches?.(EXCLUDE_SELECTOR) && isVisible(el)) {
        rawFocusables.push(el);
      }
      if (el.shadowRoot && el.shadowRoot.mode === 'open') walk(el.shadowRoot);
      if (el.tagName === 'SLOT') {
        (el as HTMLSlotElement).assignedElements({ flatten: true }).forEach(walk);
      }
    }

    const { children } = node as ParentNode;
    if (children) Array.from(children).forEach(walk);
  };

  walk(panel);

  // First collapse: any focusable found inside a grid -> a single entry for that grid host.
  const afterGrid: HTMLElement[] = [];
  const gridsSeen = new Set<HTMLElement>();
  rawFocusables.forEach((el) => {
    const grid = isGridHost(el) ? el : (getAncestorsAcrossShadow(el).find((node) => isGridHost(node as Element)) as HTMLElement | undefined);
    if (grid) {
      if (!gridsSeen.has(grid)) {
        gridsSeen.add(grid);
        afterGrid.push(grid);
      }
    } else {
      afterGrid.push(el);
    }
  });

  // Second collapse: composite controls (e.g. he-combobox) expose several focusables inside
  // their own shadow root; keep only the first so the control is a single tab stop. Structural
  // containers (he-card, etc.) project their content via slots — that content lives in light DOM,
  // not the container's shadow — so it is correctly left as separate stops.
  const stops: HTMLElement[] = [];
  const controlHostsSeen = new Set<HTMLElement>();
  afterGrid.forEach((el) => {
    if (isGridHost(el)) {
      stops.push(el);
      return;
    }
    const host = customElementShadowHost(el);
    if (host && host !== panel) {
      if (!controlHostsSeen.has(host)) {
        controlHostsSeen.add(host);
        stops.push(el);
      }
    } else {
      stops.push(el);
    }
  });
  return stops;
};

/** Index of the stop that is, contains, or is contained by the given element. */
const tabStopIndexOf = (stops: HTMLElement[], el: Node | null): number => {
  if (!el) return -1;
  const ancestors = getAncestorsAcrossShadow(el);
  // `stop === el`            : the element is itself a stop (e.g. a leaf <a>/<button>)
  // `ancestors.includes(stop)`: the element lives inside a stop (e.g. a grid cell -> grid host)
  // `stop inside el`          : the element is a control host whose stop is its inner focusable
  //                            (focusin retargets across shadow to the host, e.g. HE-BUTTON_MEM)
  return stops.findIndex((stop) => stop === el || ancestors.includes(stop) || getAncestorsAcrossShadow(stop).includes(el));
};

/** Focuses a stop. Grids delegate to their inner `<table>`; others focus directly. */
const focusTabStop = (stop: HTMLElement): void => {
  if (isGridHost(stop) && stop.shadowRoot) {
    const table = stop.shadowRoot.querySelector<HTMLElement>('table[role="grid"]') ?? stop.shadowRoot.querySelector<HTMLElement>('table');
    (table ?? stop).focus();
    return;
  }
  stop.focus();
};

let isInstalled = false;
// Set by the backstop when it handles a keydown-driven escape so the owner skips the
// same in-flight keydown (the backstop's focusin runs before the owner's keydown).
let backstopHandledKeydown = false;

const handleKeyDown = (event: KeyboardEvent): void => {
  if (event.key !== 'Tab') return;

  if (backstopHandledKeydown) {
    backstopHandledKeydown = false;
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  const panel = getOpenFlyInPanel();
  if (!panel) return;
  const from = getDeepActiveElement();
  if (!isWithinPanel(panel, from)) return;

  const stops = collectPanelTabStops(panel);
  if (stops.length === 0) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const count = stops.length;
  const step = event.shiftKey ? -1 : 1;
  let index = tabStopIndexOf(stops, from);
  if (index < 0) index = event.shiftKey ? 0 : -1;

  // Move to the next stop, skipping any that fail to take focus.
  let nextIndex = index;
  for (let attempt = 0; attempt < count; attempt += 1) {
    nextIndex = (nextIndex + step + count) % count;
    const target = stops[nextIndex];
    focusTabStop(target);
    const landed = getDeepActiveElement();
    if (tabStopIndexOf(stops, landed) === nextIndex && landed !== from) break;
  }
};

const handleFocusInBackstop = (event: FocusEvent): void => {
  const target = event.target as Element | null;
  // Only react to Tabster's phantom-dummy escape (keyboard); ignore pointer focus moves.
  if (!target?.matches?.(TABSTER_DUMMY_SELECTOR)) return;

  const panel = getOpenFlyInPanel();
  if (!panel) return;
  const relatedTarget = event.relatedTarget as Element | null;
  if (!relatedTarget || !isWithinPanel(panel, relatedTarget)) return; // focus did not leave from the panel

  const stops = collectPanelTabStops(panel);
  if (stops.length === 0) return;
  const fromIndex = tabStopIndexOf(stops, relatedTarget);
  if (fromIndex < 0) return;

  const count = stops.length;
  // Direction from DOM order: a dummy positioned before the source means we went backward.
  const backward = !!(target.compareDocumentPosition(relatedTarget) & Node.DOCUMENT_POSITION_FOLLOWING);
  const targetIndex = (fromIndex + (backward ? -1 : 1) + count) % count;

  event.stopImmediatePropagation();
  focusTabStop(stops[targetIndex]);

  backstopHandledKeydown = true;
  window.setTimeout(() => {
    backstopHandledKeydown = false;
  }, 0);
};

/** Installs the focus trap listeners. Idempotent. */
export const enableMsxFlyInFocusTrap = (): void => {
  if (isInstalled) return;
  document.addEventListener('keydown', handleKeyDown, true);
  window.addEventListener('focusin', handleFocusInBackstop, true);
  isInstalled = true;
};

/** Removes the focus trap listeners. Idempotent. */
export const disableMsxFlyInFocusTrap = (): void => {
  if (!isInstalled) return;
  document.removeEventListener('keydown', handleKeyDown, true);
  window.removeEventListener('focusin', handleFocusInBackstop, true);
  isInstalled = false;
  backstopHandledKeydown = false;
};

/** Exposed for unit testing only. */
export const __testables = { collectPanelTabStops, tabStopIndexOf, getOpenFlyInPanel };
